import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { CaseBarrier, BarrierType } from '~/types/coordinate';

interface Props {
  barriers: CaseBarrier[];
  onResolve: (barrierId: string) => void;
}

export const BARRIER_LABELS: Record<BarrierType, string> = {
  missing_doc:         'Missing Document',
  payer_auth:          'Payer Authorization',
  patient_preference:  'Patient Preference',
  clinical_complexity: 'Clinical Complexity',
  family_conflict:     'Family Conflict',
  capacity:            'Provider Capacity',
  no_match:            'No Match Found',
};

export const BARRIER_COLORS: Record<BarrierType, { bgcolor: string; color: string }> = {
  missing_doc:         { bgcolor: '#fff3e0', color: '#e65100' },
  payer_auth:          { bgcolor: '#f0eef8', color: '#635f99' },
  patient_preference:  { bgcolor: '#e3f2fd', color: '#1565c0' },
  clinical_complexity: { bgcolor: '#f0eef8', color: '#635f99' },
  family_conflict:     { bgcolor: '#fff8e1', color: '#f57f17' },
  capacity:            { bgcolor: '#ffebee', color: '#b71c1c' },
  no_match:            { bgcolor: '#ffebee', color: '#b71c1c' },
};

const BarriersPanel: React.FC<Props> = ({ barriers, onResolve }) => {
  const active = barriers.filter(b => !b.resolved);
  const resolved = barriers.filter(b => b.resolved);

  return (
    <Box>

      {barriers.length === 0 ? (
        <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: 'center' }}>
          No barriers flagged
        </Typography>
      ) : (
        <Box>
          {active.map(barrier => {
            const colors = BARRIER_COLORS[barrier.type];
            return (
              <Box
                key={barrier.id}
                sx={{
                  p: 1.25, mb: 1, borderRadius: 1,
                  border: '1px solid',
                  borderColor: colors.bgcolor,
                  bgcolor: colors.bgcolor + '60',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Chip
                      label={BARRIER_LABELS[barrier.type]}
                      size="small"
                      sx={{ height: 17, fontSize: '0.62rem', fontWeight: 700, bgcolor: colors.bgcolor, color: colors.color, mb: 0.75 }}
                    />
                    <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{barrier.description}</Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.68rem' }}>
                      Flagged {new Date(barrier.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
                    onClick={() => onResolve(barrier.id)}
                    sx={{ ml: 1, flexShrink: 0, fontSize: '0.72rem' }}
                  >
                    Resolve
                  </Button>
                </Stack>
              </Box>
            );
          })}

          {resolved.length > 0 && (
            <>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5, mb: 0.75 }}>
                Resolved
              </Typography>
              {resolved.map(barrier => (
                <Box
                  key={barrier.id}
                  sx={{ p: 1.25, mb: 0.75, borderRadius: 1, border: '1px solid', borderColor: 'divider', opacity: 0.55 }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'success.main' }} />
                    <Chip
                      label={BARRIER_LABELS[barrier.type]}
                      size="small"
                      sx={{ height: 17, fontSize: '0.62rem', fontWeight: 600, bgcolor: '#f5f5f5', color: '#616161' }}
                    />
                  </Stack>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', textDecoration: 'line-through' }}>
                    {barrier.description}
                  </Typography>
                </Box>
              ))}
            </>
          )}
        </Box>
      )}

    </Box>
  );
};

export default BarriersPanel;
